import { log, logE } from "../../shared/services/debug-logger";
import type { AdminOnboardingRow } from "../models/admin-onboarding.models";
import {
  deleteAdminOnboardingVessel,
  getAdminOnboardingErrorMessage,
  getAdminOnboardingRows,
} from "../services/admin-onboarding.service";
import {
  getAdminOnboardingDeleteMessage,
  getAdminOnboardingVesselRows,
  toAdminOnboardingDeleteRequest,
  toAdminOnboardingRows,
} from "../utils/admin-onboarding.utils";
import { useCallback, useEffect, useRef, useState } from "react";

// Owns the table and confirmation snapshot for this page only.
// Cancels reads on refresh or unmount and ignores completed writes after leaving the page.
export function useAdminOnboarding() {
  const [rows, setRows] = useState<AdminOnboardingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [confirmationRows, setConfirmationRows] = useState<AdminOnboardingRow[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const readController = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const deletionInProgress = useRef(false);

  const refresh = useCallback(async () => {
    log("STATE - refresh - onboarding");
    readController.current?.abort();
    const controller = new AbortController();
    readController.current = controller;
    setLoading(true);
    setErrorMessage(null);

    try {
      const records = await getAdminOnboardingRows(controller.signal);

      if (!controller.signal.aborted) {
        log("STATE - refresh - onboarding applied", { count: records.length });
        setRows(toAdminOnboardingRows(records));
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        logE(new Error("STATE - refresh - onboarding failed"));
        setErrorMessage(getAdminOnboardingErrorMessage(error));
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();

    return () => {
      mounted.current = false;
      readController.current?.abort();
    };
  }, [refresh]);

  const selectVessel = useCallback(
    (vessel: AdminOnboardingRow) => {
      log("STATE - selectVessel - onboarding confirmation");
      setConfirmationRows(getAdminOnboardingVesselRows(rows, vessel));
      setModalError(null);
      setActionMessage(null);
    },
    [rows],
  );

  const closeConfirmation = useCallback(() => {
    if (!deletionInProgress.current) {
      log("STATE - closeConfirmation - onboarding");
      setConfirmationRows([]);
      setModalError(null);
    }
  }, []);

  const confirmDeletion = useCallback(async () => {
    if (!confirmationRows.length || deletionInProgress.current) {
      log("STATE - confirmDeletion - skipped", { pending: deletionInProgress.current });
      return;
    }

    log("STATE - confirmDeletion", { count: confirmationRows.length });
    deletionInProgress.current = true;
    setDeleting(true);
    setModalError(null);

    try {
      const request = toAdminOnboardingDeleteRequest(confirmationRows);
      const result = await deleteAdminOnboardingVessel(request);
      log("STATE - confirmDeletion - completed");

      if (mounted.current) {
        setRows((current) => current.filter((row) => !request.recordKeys.includes(row.RECORD_KEY)));
        setConfirmationRows([]);
        setActionMessage(getAdminOnboardingDeleteMessage(result));
        await refresh();
      }
    } catch (error) {
      logE(new Error("STATE - confirmDeletion - failed"));
      if (mounted.current) {
        setModalError(getAdminOnboardingErrorMessage(error));
      }
    } finally {
      deletionInProgress.current = false;

      if (mounted.current) {
        setDeleting(false);
      }
    }
  }, [confirmationRows, refresh]);

  return {
    rows,
    loading,
    errorMessage,
    actionMessage,
    confirmationRows,
    deleting,
    modalError,
    refresh,
    selectVessel,
    closeConfirmation,
    confirmDeletion,
  };
}
